import React from 'react';

import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { dashboardUrl } from '@/config/urls';
import { useAuth } from '@/providers/AuthProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { FormHelperText } from '@material-ui/core';
import axios from 'axios';
import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import styles from '../../Login/LoginPage.module.scss';

type LoginResponse = {
	jwt: string;
	userId: string;
	teamId: string;
	user: {
		email: string;
		id: string;
		displayName: string;
	};
};

const signUpValidationSchema = Yup.object().shape({
	code: Yup.string().required('Please enter your code'),
});

const VerifyForm: React.FC<{
	email: string;
	onChange: (email: string | null) => void;
}> = ({ email, onChange }) => {
	const {
		api: { url },
	} = useEnvironment();
	const { authenticate } = useAuth();
	const { token } = useParams();
	const navigate = useNavigate();

	return (
		<Formik
			key="asaphSignUn"
			initialValues={{
				code: '',
			}}
			validationSchema={signUpValidationSchema}
			onSubmit={async (values, formikHelpers) => {
				if (values.code.toString().length < 4) {
					formikHelpers.setSubmitting(false);
					return formikHelpers.setFieldError(
						'code',
						'Code is invalid',
					);
				}
				try {
					const res = await axios.post<LoginResponse>(
						`${url}/link/accept/${token}`,
						{
							email,
							code: values.code.toString(),
						},
					);

					if (!res.data.jwt) {
						throw new Error('Response did not produce a valid jwt');
					}
					// We need to navigate to the `dashboardUrl` here before authenticating
					// because otherwise after the user is logged in, the route would remain
					// the invite one.
					navigate(dashboardUrl);
					authenticate(res.data.jwt);
				} catch (err) {
					console.error(err);
					formikHelpers.setErrors({
						code: 'Invalid code',
					});
				}
				formikHelpers.setSubmitting(false);
			}}
		>
			{({
				handleSubmit,
				handleChange,
				values,
				isSubmitting,
				setFieldValue,
				touched,
				errors,
			}) => (
				<form onSubmit={handleSubmit} id="asaphSignIn">
					<h1 className={styles.heading}>Verification</h1>
					<p className={styles.text}>
					One-time code sent to {' '}
						<span>
						{email}
						</span>
					</p>
					<div className="text-center">
						<FormInput
							id="code"
							name="code"
							type="number"
							placeholder="1234"
							onChange={handleChange}
							value={values.code}
							className={styles['code-input']}
						/>
					</div>
					<div className="vsb-8" />
					<AsaphButton
						label="Verify"
						type="submit"
						color="blue"
						fullWidth
						disabled={isSubmitting}
					/>
					<p className={styles.smallText}>
					Wrong email? {' '}
						<span
							className={styles.link}
							onClick={() => onChange(null)}
						>
							 Use A Different Email
						</span>{' '}
					</p>
					<div className="vsb-20">
						{!!errors.code && touched.code && (
							<FormHelperText
								error={true}
								classes={{
									root: 'authentication-error-text',
								}}
							>
								{errors.code}
							</FormHelperText>
						)}
					</div>
				</form>
			)}
		</Formik>
	);
};

export default VerifyForm;
